import { ChevronRight } from '@mui/icons-material';
import {
    Box,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import DashboardCard from '/@/components/bayer/dashboard-card';
import Tabs from '/@/components/bayer/tabs';
import Icon from '/@/components/icon';
import { useRVRecommendationsDashboard } from '/@/hooks/dashboards';
import { useGetUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getCatalogBrandsByCrop } from '/@/store/catalog/selectors';
import { reverse } from '/@/utils/router';

const RVNameCell = ({ userNodeId }: { userNodeId: number }) => {
    const userNodeQuery = useGetUserNode(userNodeId);
    return <TableCell>{userNodeQuery.data?.name || '-'}</TableCell>;
};

const RVsRecommnedationCard = ({ userNodeId }: { userNodeId: number }) => {
    const navigate = useNavigate();
    const [selectedTab, setSelectedTab] = useState<'hybrid' | 'protection'>('hybrid');

    const tabs: Record<
        typeof selectedTab,
        { label: string; crops: string[]; columns: { icon?: React.ReactElement; label?: string; types: string[] }[] }
    > = {
        hybrid: {
            label: _('Híbridos'),
            crops: ['corn', 'soy', 'corn_density'],
            columns: [
                {
                    label: _('Híbridos'),
                    types: ['corn', 'soy'],
                },
                {
                    label: _('Densidad'),
                    types: ['density'],
                },
            ],
        },
        protection: {
            label: _('Protección'),
            crops: ['weed', 'disease', 'plague', 'seed_treatment'],
            columns: [
                {
                    label: _('Fungicidas'),
                    icon: (
                        <Tooltip title={_('Fungicidas')}>
                            <Box>
                                <Icon type='diseaseRecommendation' size='xxs' padding={false} />
                            </Box>
                        </Tooltip>
                    ),
                    types: ['funguicide'],
                },
                {
                    label: _('Insecticidas'),
                    icon: (
                        <Tooltip title={_('Insecticidas')} placement='top'>
                            <Box>
                                <Icon type='plagueRecommendation' size='xxs' padding={false} />
                            </Box>
                        </Tooltip>
                    ),
                    types: ['insecticide'],
                },
                {
                    label: _('Herbicidas'),
                    icon: (
                        <Tooltip title={_('Herbicidas')}>
                            <Box>
                                <Icon type='weedRecommendation' size='xxs' padding={false} />
                            </Box>
                        </Tooltip>
                    ),
                    types: ['herbicide'],
                },
                {
                    label: _('Trat. Semillas'),
                    icon: (
                        <Tooltip title={_('Tratamiento de semillas')}>
                            <Box>
                                <Icon type='seedBag' size='xxs' padding={false} />
                            </Box>
                        </Tooltip>
                    ),
                    types: ['seed_treatment'],
                },
            ],
        },
    } as const;

    const recommendationsQuery = useRVRecommendationsDashboard({
        userNodeId,
        type_in: tabs[selectedTab].crops,
        select: ['base_stats'],
        excludeTargetNode: true,
    });

    const getCountForColumn = (
        countByType:
            | {
                  corn?: number;
                  soy?: number;
                  funguicide?: number;
                  insecticide?: number;
                  herbicide?: number;
                  density?: number;
              }
            | undefined,
        columnTypes: string[]
    ): number => {
        if (!countByType) return 0;
        return columnTypes.reduce((sum, type) => {
            const count = (countByType as Record<string, number | undefined>)[type] || 0;
            return sum + count;
        }, 0);
    };

    const cropProtectionIsAvailable = useSelector((state) => getCatalogBrandsByCrop(state, 'crop_protection'));
    const availableTabs = useMemo(() => {
        const tabs = [{ label: _('Híbridos'), value: 'hybrid' }];
        if (cropProtectionIsAvailable.length > 0) {
            tabs.push({ label: _('Protección'), value: 'protection' });
        }
        return tabs;
    }, [cropProtectionIsAvailable]);

    return (
        <DashboardCard
            title={_('Recomendaciones')}
            subtitle={_('Visualizá la cantidad de recomendaciones creadas por los RV')}
            isLoading={recommendationsQuery.isLoading}
            buttonProps={{
                children: _('Ver todas las recomendaciones'),
                onClick: () => {
                    navigate(reverse('RVs.list', { qs: { tab: 'recommendations' } }));
                },
            }}
        >
            <Stack spacing={2} height='100%' sx={{ overflowX: 'auto' }}>
                <Tabs
                    options={availableTabs}
                    value={selectedTab}
                    setValue={(value) => setSelectedTab(value as 'hybrid' | 'protection')}
                    variant='chips'
                />
                <Table
                    sx={{
                        '& .MuiTableCell-root': {
                            color: (t) => t.palette.primary.main,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Typography variant='subtitle2' fontWeight='bold'>
                                    {_('RV')}
                                </Typography>
                            </TableCell>
                            {tabs[selectedTab].columns.map((column) => (
                                <TableCell key={column.label}>
                                    <Stack
                                        direction='row'
                                        spacing={0.5}
                                        alignItems='center'
                                        justifyContent={column?.icon ? 'center' : 'flex-start'}
                                    >
                                        {column?.icon ? (
                                            column.icon
                                        ) : (
                                            <Typography variant='subtitle2' fontWeight='bold'>
                                                {column.label}
                                            </Typography>
                                        )}
                                    </Stack>
                                </TableCell>
                            ))}
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {recommendationsQuery.data?.recommendationsByRv?.map((rv) => (
                            <TableRow key={rv.userNodeId}>
                                <RVNameCell userNodeId={rv.userNodeId} />
                                {tabs[selectedTab].columns.map((column) => (
                                    <TableCell key={column.label} align={column?.icon ? 'center' : 'left'}>
                                        {getCountForColumn(rv.countByType, column.types)}
                                    </TableCell>
                                ))}
                                <TableCell align='right'>
                                    <IconButton
                                        size='small'
                                        color='secondary'
                                        onClick={() => {
                                            navigate(
                                                reverse('RVs.detail', {
                                                    params: {
                                                        rvId: rv.userNodeId,
                                                    },
                                                    qs: {
                                                        tab: 'recommendations',
                                                    },
                                                })
                                            );
                                        }}
                                    >
                                        <ChevronRight />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Stack>
        </DashboardCard>
    );
};

export default RVsRecommnedationCard;
